import React from 'react';
import PropTypes from 'prop-types';
import CwTextField from '../cwtextfield/CwTextField';
import CwLabel from "../cwlabel/CwLabel";
import css from './CwCurrencyField.scss';
import {
  formatToAFloat, checkNextPropsWhileNotTyping, checkAcceptableChangeState, checkDecimalAgainstMaxLength,
  undesirableCurrencyFieldStates, generateCurrencyRegex, checkforOneDecimalAndMaxDecimalUnits, generateMaxLength,
  typeOf,
} from 'src/utils/NumberUtils';
import { AlignmentEnum } from '../../utils/Enums';
import { Includes } from '../..//utils/PolyFillFunctions';

class CwCurrencyField extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      value: this.formatDisplayValueWithoutStateChange(this.props.value),
      regex: generateCurrencyRegex(this.props.negative, this.props.decimal),
    };
    this._typing = false;
    this._focused = false;
  }

  /**
   * Handle getting an empty string as props
   */
  componentWillReceiveProps(nextProps) {
    if (checkNextPropsWhileNotTyping(nextProps.value, this._focused)) {
      this.setState({
        value: this.formatDisplayValueWithoutStateChange(nextProps.value),
      });
    } else if ((nextProps.value || nextProps.value === '' || nextProps.value === 0) && (!this.state.value || this.state.value === '')) {
      this.setState({
        value: nextProps.value.toString(),
      });
    }
  }

  componentDidUpdate() {
    if (this.props.selectAllOnFocus && this._focused && !this._typing) {
      this.textField.inputField.select();
    }
  }

  /**
   * Handle exceptions before passing the change value up the chain
   */
  handleChange = (value) => {
    //Keep acceptable states and bypass all other checks
    if (checkAcceptableChangeState(value, this.props.negative, this.props.decimal)) {
      this.setState({
        value: value,
      });
      this.props.onChange(null);
      return;
    } else if (undesirableCurrencyFieldStates(value, this.props.negative, this.props.decimal)) {
      //Handle undesired states and restrict them on key entry
      this.props.onChange(null);
      return;
    }

    //Do not allow more units after decimal than maxDecimalUnits plus period and only allow one decimal
    if (checkforOneDecimalAndMaxDecimalUnits(value, this.props.decimal, this.props.maxDecimalUnits)) {
      this.restrictLastDigit(value);
      return;
    }

    //Restrict left field by max length prop unless the last digit is a decimal, then allow extra char. 
    if (!checkDecimalAgainstMaxLength(value, this.props.decimal,
      generateMaxLength(this.state.value, this.props.maxLength, this.props.decimal, this.props.maxDecimalUnits))) {
      this.restrictLastDigit(value);
      return;
    }

    //Allow no more than 1 negative symbol
    if (Includes(value, "-") || Includes(value, this.props.negative)) {
      if (value.match(new RegExp("[-" + this.props.negative + "]", "g")).length !== 1) {
        this.restrictLastDigit(value);
        return;
      }
    }
    this.setState({
      value: value,
    });
    const newVal = this.formatToFloat(value);
    if (isNaN(newVal) || typeof newVal === 'undefined') {
      this.props.onChange(null);
    } else {
      this.props.onChange(newVal);
    }
  };

  //This method gets called when more then one negative or decimal are used
  restrictLastDigit = (value) => {
    const newVal = value.substring(0, value.length - 1);
    this.setState({
      value: newVal,
    });
    if (isNaN(this.formatToFloat(newVal))) {
      this.props.onChange(null);
      return;
    }
    this.props.onChange(this.formatToFloat(newVal));
  };

  /**
   * onBlur, convert float or int to a currency string.
   */
  handleBlur = () => {
    this._focused = false;

    if (this.state.value || this.state.value === 0) {
      this.formatDisplayValue(this.state.value);
    }

    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  /**
   * On focus, convert string to a float.
   */
  handleFocus = () => {
    this._focused = true;
    this._typing = false;

    if (this.state.value) {

      //First let's get a float to work with, stripping away all special characters
      let newVal = this.formatToFloat(this.state.value);

      if (isNaN(newVal)) {
        this.setState({
          value: '',
        });
        return;
      }
      // Now lets convert that float back to a string and add in the correct decimal, but only if it's not "."
      if (this.props.decimal !== ".") {
        newVal = newVal.toString().replace('.', this.props.decimal);
      }

      //Finally set the state
      this.setState({
        value: newVal.toString(),
      });
    }
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleKeyDown = () => {
    this._typing = true;
  }

  /**
   * After converting a number from a string, we need to update the state and call onChange
   *
   */
  formatDisplayValue = (value) => {

    const newVal = this.formatDisplayValueWithoutStateChange(value);

    const floatNewVal = this.formatToFloat(newVal);

    if (isNaN(floatNewVal)) {
      this.props.onChange(null);
    } else {
      this.props.onChange(floatNewVal);
    }

    this.setState({
      value: newVal,
    });

    return newVal;
  };

  /**
   * Converts a number or a string to to be displayed via the localized
   * formatNumber method provided by ReactIntl.
   */
  formatDisplayValueWithoutStateChange = (value) => {

    let newVal;
    if (typeOf(value) === 'String') {
      let temp = value.split(this.props.thousands).join('');
      temp = temp.replace(this.props.symbol, '');
      temp = temp.replace(this.props.decimal, '.');
      temp = parseFloat(temp);
      newVal = this.props.formatToCurrency(temp);
    } else if (isNaN(value) || value === undefined) {
      newVal = '';
    } else if (typeOf(value) === 'Number') {
      newVal = this.props.formatToCurrency(value);
    }

    //Check for NaN
    if (isNaN(this.formatToFloat(newVal))) {
      return '';
    }
    return newVal;
  };

  /**
   * Format's a string to a float with special handling for currency characters
   * such as $ and , within the string. These characters should be provided to the method
   * so that it knows what to strip out. It also replaces the local decimal separator
   * with a . for the sake of parseFloat
   */
  formatToFloat = (value) => {
    if (!value || typeof value !== "string") {
      return;
    }
    let temp = value.replace(this.props.symbol, '');
    return formatToAFloat(temp, this.props.thousands, this.props.decimal, this.props.negative);
  };

  render() {

    return (
      <div data-cwid={this.props.cwId} className={css.currencyRoot}>
        <CwTextField
          ref={(item) => this.textField = item}
          className={css.currencyText}
          value={this.state.value}
          onBlur={this.handleBlur}
          onFocus={this.handleFocus}
          onChange={this.handleChange}
          regex={this.state.regex}
          maxLength={generateMaxLength(this.state.value, this.props.maxLength, this.props.decimal, this.props.maxDecimalUnits)}
          placeholder={this.props.placeholder}
          readOnly={this.props.readOnly}
          tabIndex={this.props.tabIndex}
          invalid={this.props.invalid}
          disabled={this.props.disabled}
          id={this.props.id}
          onKeyPress={this.props.onKeyPress}
          onKeyDown={this.handleKeyDown}
          textAlignment={this.props.textAlignment ? this.props.textAlignment : this.props.inAGrid ? AlignmentEnum.RIGHT : undefined}
        />
        <CwLabel className={css.currencyCode}>{this.props.currency}</CwLabel>
      </div>
    );
  }
  ;
}

CwCurrencyField.propTypes = {
  /**
   * Connectwise element id to be used as unique identifier
   */
  cwId: PropTypes.string,
  /**
   * Text in textfield, should always come in as a number, we will handle formatting display.
   */
  value: PropTypes.number,
  /**
   * Hint text, displayed when user has not typed anything in
   */
  placeholder: PropTypes.string,
  /**
   * Used to set textfield to a read only mode
   */
  readOnly: PropTypes.bool,
  /**
   * Callback function that is fired when textfield's text changes
   */
  onChange: PropTypes.func,
  /**
   * Used to control order when tabbing through items
   */
  tabIndex: PropTypes.number,
  /**
   * Used to control if text is valid
   */
  invalid: PropTypes.bool,
  /**
   * Used to disable textfield
   */
  disabled: PropTypes.bool,
  /**
   * Used as an identifier for the component
   */
  id: PropTypes.string,
  /**
   * Used to limit the inputs of the field by matching against provided string.
   */
  regex: PropTypes.string,
  /**
   * Callback function that is fired when textfield is focused
   */
  onFocus: PropTypes.func,
  /**
   * Callback function that is fired when textfield is blurred
   */
  onBlur: PropTypes.func,
  /**
   * Callback function that is fired on key press
   */
  onKeyPress: PropTypes.func,
  /**
   * The currency symbol of the current locale.
   * Recommend that this is acquired from React-Intl.
   */
  symbol: PropTypes.string.isRequired,
  /**
   * The decimal separator for the current locale.
   * This can be provided from React or Java Intl.
   */
  decimal: PropTypes.string.isRequired,
  /**
   * The thousands separator of the current locale
   * and is usually " ", ",", "." We recommend acquiring
   * this from React-Intl
   */
  thousands: PropTypes.string.isRequired,

  /**
   * The negative symbol used by the current locale. Should
   * typically be a minus sign but unicode can vary
   */
  negative: PropTypes.string.isRequired,
  /**
   * The maximum number of places after the decimal
   * which the currency accepts. This will usually be 2 or 3
   */
  maxDecimalUnits: PropTypes.number.isRequired,
  /**
   * This function should format your float to a currency string value
   * We strongly suggest using React-Intl
   */
  formatToCurrency: PropTypes.func.isRequired,
  /**
   * The max number of characters allowed in the field.
   * Should not exceed 13 excluding decimals and fractions
   */
  maxLength: PropTypes.number,
  /**
   * The currency for the label.
   */
  currency: PropTypes.string,
  /**
   * Used to control if all text is highlighted on focus
   */
  selectAllOnFocus: PropTypes.bool
};

CwCurrencyField.defaultProps = {};

export default CwCurrencyField;


// WEBPACK FOOTER //
// ./src/components/cwcurrencyfield/CwCurrencyField.jsx